import { useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Truck,
  FolderOpen,
  FileText,
  Users,
  Building2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  FileDown,
  Info,
  DollarSign,
  Receipt,
  Mail,
  Train,
  Database,
  Loader2,
  Link2,
  Copy,
  ExternalLink,
} from "lucide-react";


const ImportExportPage = () => {
  const [token] = useState(localStorage.getItem("token"));
  const [importing, setImporting] = useState(null);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [metransExporting, setMetransExporting] = useState(false);
  const [metransImporting, setMetransImporting] = useState(false);
  const [metransResults, setMetransResults] = useState(null);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [hrLinkGenerating, setHrLinkGenerating] = useState(false);
  const [generatedHrLink, setGeneratedHrLink] = useState(null);

  const modules = [
    {
      id: "transport-orders",
      name: "Transport Orders",
      icon: Truck,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
      description: "Import/export trucking orders",
      keyField: "order_number",
      requiredCols: ["order_number", "status", "contact_person"],
    },
    {
      id: "position-folders",
      name: "Position Folders",
      icon: FolderOpen,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50",
      description: "Import/export PF records",
      keyField: "pf_number",
      requiredCols: ["pf_number", "customer_name", "status"],
    },
    {
      id: "bl-documents",
      name: "Bills of Lading",
      icon: FileText,
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      description: "Import/export BL documents",
      keyField: "bl_number",
      requiredCols: ["bl_number", "shipper_exporter", "consignee"],
    },
    {
      id: "crm",
      name: "CRM (Contacts & Companies)",
      icon: Users,
      color: "text-amber-500",
      bgColor: "bg-amber-50",
      description: "Unified import - contacts with companies",
      keyField: "email",
      requiredCols: ["email", "company_name"],
    },
    {
      id: "crm-trinet",
      name: "CRM from Trinet",
      icon: Building2,
      color: "text-rose-500",
      bgColor: "bg-rose-50",
      description: "Import Trinet customers export",
      keyField: "Id (Trinet)",
      requiredCols: ["Id", "Name", "DisplayName"],
      isTrinet: true,
    },
    {
      id: "grn-payments",
      name: "GRN Customs Payments",
      icon: FileSpreadsheet,
      color: "text-red-500",
      bgColor: "bg-red-50",
      description: "Import weekly customs (GRN) payments from XLS",
      keyField: "reference",
      requiredCols: ["Terjatev/Obveznost", "Dat. zapadlosti", "Znesek"],
      isGRN: true,
    },
    {
      id: "newsletter-emails",
      name: "Newsletter Email List",
      icon: Mail,
      color: "text-purple-500",
      bgColor: "bg-purple-50",
      description: "Import email subscribers for newsletters (email, name, group)",
      keyField: "email",
      requiredCols: ["email"],
      isNewsletter: true,
    },
    {
      id: "ap-invoices",
      name: "AP (Accounts Payable)",
      icon: DollarSign,
      color: "text-orange-500",
      bgColor: "bg-orange-50",
      description: "Import vendor invoices and payables",
      keyField: "invoice_number",
      requiredCols: ["invoice_number", "vendor_name", "amount", "due_date"],
      isAP: true,
      isLegacy: true, // Mark as legacy, replaced by per-vertical
    },
    {
      id: "ar-invoices",
      name: "AR (Accounts Receivable)",
      icon: Receipt,
      color: "text-green-500",
      bgColor: "bg-green-50",
      description: "Import customer invoices and receivables",
      keyField: "invoice_number",
      requiredCols: ["invoice_number", "customer_name", "amount", "due_date"],
      isAR: true,
      isLegacy: true, // Mark as legacy, replaced by per-vertical
    },
    {
      id: "staff-directory",
      name: "Staff Directory",
      icon: Users,
      color: "text-indigo-500",
      bgColor: "bg-indigo-50",
      description: "Import/export employee records (HR)",
      keyField: "surname",
      requiredCols: ["Surname", "First Name", "Email"],
      isStaff: true,
    },
  ];

  // AR/AP verticals for per-company imports
  const arApVerticals = [
    { id: "ep_holding", name: "EP Holding", color: "text-blue-600", bgColor: "bg-blue-50" },
    { id: "logistika", name: "Europacific", color: "text-emerald-600", bgColor: "bg-emerald-50" },
    { id: "cargo_cfs", name: "Cargo CFS", color: "text-orange-600", bgColor: "bg-orange-50" },
    { id: "epas", name: "EPA&S", color: "text-pink-600", bgColor: "bg-pink-50" },
    { id: "artcom", name: "Artcom EPT", color: "text-purple-600", bgColor: "bg-purple-50" },
  ];

  const handleImport = useCallback(async (moduleId, file) => {
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    if (!filename.endsWith('.csv') && !filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    setImporting(moduleId);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let apiUrl = `${API_URL}/import/${moduleId}?update_existing=true`;
      
      // Handle special import endpoints
      if (moduleId === 'grn-payments') {
        apiUrl = `${API_URL}/vendor-ap/grn-payments/import`;
      } else if (moduleId === 'newsletter-emails') {
        apiUrl = `${API_URL}/linkedin-newsletter/subscribers/import`;
      } else if (moduleId === 'ap-invoices') {
        apiUrl = `${API_URL}/vendor-ap/invoices/import`;
      } else if (moduleId === 'ar-invoices') {
        apiUrl = `${API_URL}/ar/invoices/import`;
      } else if (moduleId.startsWith('ar-')) {
        // Per-vertical AR import: ar-ep_holding, ar-logistika, etc.
        const verticalId = moduleId.replace('ar-', '');
        apiUrl = `${API_URL}/ar-ap/ar/import/${verticalId}`;
      } else if (moduleId.startsWith('ap-') && moduleId !== 'ap-invoices') {
        // Per-vertical AP import: ap-ep_holding, ap-logistika, etc.
        const verticalId = moduleId.replace('ap-', '');
        apiUrl = `${API_URL}/ar-ap/ap/import/${verticalId}`;
      } else if (moduleId === 'staff-directory') {
        apiUrl = `${API_URL}/staff-directory/import`;
      }

      const response = await axios.post(
        apiUrl,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResults({
        module: moduleId,
        ...response.data,
      });

      // Handle GRN payments
      if (moduleId === 'grn-payments') {
        const { imported, errors } = response.data;
        if (errors && errors.length > 0) {
          toast.warning(`GRN import complete with ${errors.length} errors. Check results.`);
        } else {
          toast.success(`Imported ${imported} GRN customs payments`);
        }
      }
      // Handle Newsletter email import
      else if (moduleId === 'newsletter-emails') {
        const { imported, updated, skipped, total, errors } = response.data;
        if (errors && errors.length > 0) {
          toast.warning(`Email import complete with ${errors.length} errors. Check results.`);
        } else {
          toast.success(`Email subscribers imported: ${imported} new, ${updated} updated, ${skipped} skipped`);
        }
      }
      // Handle unified CRM response format
      else if (moduleId === 'crm') {
        const { contacts_imported, contacts_updated, companies_created, companies_updated, skipped, errors } = response.data;
        if (errors && errors.length > 0) {
          toast.warning(`Import complete with ${errors.length} errors. Check results.`);
        } else {
          toast.success(`Contacts: ${contacts_imported} new, ${contacts_updated} updated | Companies: ${companies_created} new, ${companies_updated} updated`);
        }
      } else if (moduleId === 'crm-trinet') {
        const { companies_created, companies_updated, contacts_created, skipped, errors } = response.data;
        if (errors && errors.length > 0) {
          toast.warning(`Trinet import complete with ${errors.length} errors. Check results.`);
        } else {
          toast.success(`Companies: ${companies_created} new, ${companies_updated} updated | Contacts: ${contacts_created} created`);
        }
      } else {
        const { imported, updated, skipped, errors } = response.data;
        if (errors && errors.length > 0) {
          toast.warning(`Import complete with ${errors.length} errors. Check results.`);
        } else {
          toast.success(`Import complete: ${imported} new, ${updated} updated, ${skipped} skipped`);
        }
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(null);
    }
  }, [token]);

  const handleExport = async (moduleId, format = 'xlsx') => {
    try {
      let url = `${API_URL}/export/${moduleId}?format=${format}`;
      
      // Handle CRM - export both contacts and companies
      if (moduleId === 'crm') {
        // Export contacts (which includes company info)
        url = `${API_URL}/export/crm?type=contacts&format=${format}`;
      }
      // Handle Staff Directory export
      else if (moduleId === 'staff-directory') {
        url = `${API_URL}/staff-directory/export`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${moduleId}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Exported ${moduleId} as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed");
    }
  };

  const handleDownloadTemplate = async (moduleId, format = 'xlsx') => {
    try {
      const response = await axios.get(
        `${API_URL}/import/template/${moduleId}?format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${moduleId}_template.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Downloaded ${moduleId} template`);
    } catch (error) {
      console.error("Template download error:", error);
      toast.error("Failed to download template");
    }
  };

  const handleDrop = (e, moduleId) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImport(moduleId, file);
    }
  };

  // Generate HR update link
  const handleGenerateHrLink = async () => {
    setHrLinkGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/staff-directory/generate-hr-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedHrLink(response.data);
      toast.success("HR update link generated! Valid for 7 days.");
    } catch (error) {
      console.error("Error generating HR link:", error);
      toast.error("Failed to generate HR link");
    } finally {
      setHrLinkGenerating(false);
    }
  };

  // Copy HR link to clipboard
  const copyHrLink = () => {
    if (generatedHrLink?.link) {
      navigator.clipboard.writeText(generatedHrLink.link);
      toast.success("Link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800">Import / Export</h1>
            <p className="mt-2 text-slate-600">
              Import data from CSV or Excel files, or export existing data for backup
            </p>
          </div>

        {/* Info Banner */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Import Mode: Update Existing Records</p>
            <p className="mt-1">
              When importing, existing records will be updated based on the key field. 
              New records will be created for items not found in the database.
            </p>
          </div>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.filter(m => !m.isLegacy).map((module) => {
            const Icon = module.icon;
            const isImporting = importing === module.id;
            const hasResults = results?.module === module.id;

            return (
              <Card 
                key={module.id}
                className={`relative overflow-hidden transition-all ${
                  dragOver === module.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(module.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => handleDrop(e, module.id)}
                data-testid={`import-card-${module.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${module.bgColor}`}>
                      <Icon className={`h-5 w-5 ${module.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{module.name}</CardTitle>
                      <CardDescription className="text-xs">
                        Key: <code className="bg-slate-100 px-1 rounded">{module.keyField}</code>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Drop Zone */}
                  <div 
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      dragOver === module.id 
                        ? 'border-blue-400 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {isImporting ? (
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        <span>Importing...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600 mb-2">
                          Drop CSV/Excel file here
                        </p>
                        <label className="cursor-pointer">
                          <span className="text-sm text-blue-600 hover:text-blue-700">
                            or click to browse
                          </span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => handleImport(module.id, e.target.files[0])}
                            data-testid={`import-input-${module.id}`}
                          />
                        </label>
                      </>
                    )}
                  </div>

                  {/* Import Results */}
                  {hasResults && (
                    <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                      {module.id === 'crm' ? (
                        <>
                          <div className="flex items-center gap-2 text-emerald-600">
                            <Users className="h-4 w-4" />
                            <span>Contacts: {results.contacts_imported || 0} new, {results.contacts_updated || 0} updated</span>
                          </div>
                          <div className="flex items-center gap-2 text-blue-600">
                            <Building2 className="h-4 w-4" />
                            <span>Companies: {results.companies_created || 0} new, {results.companies_updated || 0} updated</span>
                          </div>
                        </>
                      ) : module.id === 'crm-trinet' ? (
                        <>
                          <div className="flex items-center gap-2 text-emerald-600">
                            <Building2 className="h-4 w-4" />
                            <span>Companies: {results.companies_created || 0} new, {results.companies_updated || 0} updated</span>
                          </div>
                          <div className="flex items-center gap-2 text-blue-600">
                            <Users className="h-4 w-4" />
                            <span>Contacts created: {results.contacts_created || 0}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>{results.imported} imported</span>
                          </div>
                          <div className="flex items-center gap-2 text-blue-600">
                            <RefreshCw className="h-4 w-4" />
                            <span>{results.updated} updated</span>
                          </div>
                        </>
                      )}
                      {results.skipped > 0 && (
                        <div className="flex items-center gap-2 text-slate-500">
                          <span>⏭️ {results.skipped} skipped</span>
                        </div>
                      )}
                      {results.errors?.length > 0 && (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertCircle className="h-4 w-4" />
                          <span>{results.errors.length} errors</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons - hide for Trinet (import only) */}
                  {!module.isTrinet && (
                    <>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleDownloadTemplate(module.id, 'xlsx')}
                          data-testid={`template-btn-${module.id}`}
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          Template
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleExport(module.id, 'xlsx')}
                          data-testid={`export-btn-${module.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Export
                        </Button>
                      </div>

                      {/* Export Format Options */}
                      <div className="flex gap-2 justify-center text-xs">
                        <button
                          onClick={() => handleExport(module.id, 'xlsx')}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <FileSpreadsheet className="h-3 w-3 inline mr-1" />
                          Excel
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          onClick={() => handleExport(module.id, 'csv')}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          CSV
                        </button>
                      </div>
                    </>
                  )}
                  {module.isTrinet && (
                    <p className="text-xs text-slate-500 text-center">
                      Import only - use Trinet's customer export
                    </p>
                  )}
                  
                  {/* Special HR Link Section for Staff Directory */}
                  {module.isStaff && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                        <Link2 className="h-4 w-4" />
                        Forward to HR
                      </div>
                      <p className="text-xs text-slate-500">
                        Generate a link that HR can open to download, edit, and upload the staff directory directly.
                      </p>
                      
                      {!generatedHrLink ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={handleGenerateHrLink}
                          disabled={hrLinkGenerating}
                          data-testid="generate-hr-link-btn"
                        >
                          {hrLinkGenerating ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Link2 className="h-4 w-4 mr-2" />
                              Generate HR Update Link
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="space-y-3 bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                            <CheckCircle className="h-4 w-4" />
                            Link ready - copy and send to HR
                          </div>
                          
                          {/* Full link in a selectable box */}
                          <div 
                            className="bg-white border border-green-300 rounded p-2 text-xs font-mono break-all cursor-text select-all"
                            onClick={(e) => {
                              // Select all text on click
                              const selection = window.getSelection();
                              const range = document.createRange();
                              range.selectNodeContents(e.target);
                              selection.removeAllRanges();
                              selection.addRange(range);
                            }}
                          >
                            {generatedHrLink.link}
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={copyHrLink}
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Link
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-green-600 text-green-700 hover:bg-green-50"
                              onClick={() => window.open(generatedHrLink.link, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Test
                            </Button>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-green-200">
                            <span>Valid until: {new Date(generatedHrLink.expires_at).toLocaleDateString()}</span>
                            <button
                              className="text-indigo-600 hover:text-indigo-800 underline"
                              onClick={() => setGeneratedHrLink(null)}
                            >
                              Generate new link
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Per-Vertical AR/AP Import Section */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">AR/AP Import by Company</h2>
          <p className="text-sm text-slate-600 mb-6">
            Import Accounts Receivable and Accounts Payable data per company vertical. 
            Uses the same format as your accounting program exports.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {arApVerticals.map((vertical) => (
              <Card key={vertical.id} className={`${vertical.bgColor} border`}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-lg ${vertical.color} flex items-center gap-2`}>
                    <Building2 className="h-5 w-5" />
                    {vertical.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* AR Import with Drag/Drop */}
                  <div 
                    className={`bg-white/80 rounded-lg p-3 transition-all ${
                      dragOver === `ar-${vertical.id}` ? 'ring-2 ring-green-400 bg-green-50' : ''
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(`ar-${vertical.id}`); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => handleDrop(e, `ar-${vertical.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-700 flex items-center gap-1">
                        <Receipt className="h-4 w-4" />
                        AR (Receivables)
                      </span>
                      {importing === `ar-${vertical.id}` && (
                        <RefreshCw className="h-4 w-4 animate-spin text-green-600" />
                      )}
                    </div>
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleImport(`ar-${vertical.id}`, e.target.files[0]);
                          }
                        }}
                        disabled={importing === `ar-${vertical.id}`}
                      />
                      <div className={`flex items-center justify-center gap-2 py-3 px-3 border-2 border-dashed rounded-lg hover:bg-green-50 transition-colors ${
                        dragOver === `ar-${vertical.id}` ? 'border-green-400 bg-green-50' : 'border-green-300'
                      }`}>
                        <Upload className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700">Drop file or click to import AR</span>
                      </div>
                    </label>
                  </div>

                  {/* AP Import with Drag/Drop */}
                  <div 
                    className={`bg-white/80 rounded-lg p-3 transition-all ${
                      dragOver === `ap-${vertical.id}` ? 'ring-2 ring-orange-400 bg-orange-50' : ''
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(`ap-${vertical.id}`); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => handleDrop(e, `ap-${vertical.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-orange-700 flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        AP (Payables)
                      </span>
                      {importing === `ap-${vertical.id}` && (
                        <RefreshCw className="h-4 w-4 animate-spin text-orange-600" />
                      )}
                    </div>
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleImport(`ap-${vertical.id}`, e.target.files[0]);
                          }
                        }}
                        disabled={importing === `ap-${vertical.id}`}
                      />
                      <div className={`flex items-center justify-center gap-2 py-3 px-3 border-2 border-dashed rounded-lg hover:bg-orange-50 transition-colors ${
                        dragOver === `ap-${vertical.id}` ? 'border-orange-400 bg-orange-50' : 'border-orange-300'
                      }`}>
                        <Upload className="h-4 w-4 text-orange-600" />
                        <span className="text-sm text-orange-700">Drop file or click to import AP</span>
                      </div>
                    </label>
                  </div>

                  {/* Show import results for this vertical */}
                  {(results?.module === `ar-${vertical.id}` || results?.module === `ap-${vertical.id}`) && (
                    <div className="bg-white rounded-lg p-3 text-xs">
                      <div className="flex items-center gap-2 mb-2">
                        {results.errors?.length > 0 ? (
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                        <span className="font-medium">
                          {results.module.startsWith('ar-') ? 'AR' : 'AP'} Import Results
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-slate-600">
                        <div>Imported: <span className="font-bold text-green-600">{results.imported || 0}</span></div>
                        <div>Updated: <span className="font-bold text-blue-600">{results.updated || 0}</span></div>
                      </div>
                      {results.errors?.length > 0 && (
                        <div className="mt-2 text-red-600">
                          {results.errors.length} error(s)
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Metrans Rail Rates Import/Export */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Train className="h-5 w-5 text-orange-500" />
            Metrans Rail Rates
          </h2>
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="py-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Export Section */}
                <div className="space-y-3">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    <Download className="h-4 w-4 text-orange-600" />
                    Export Rates
                  </h3>
                  <p className="text-sm text-slate-600">
                    Download all Metrans rail rates as JSON file for backup or migration
                  </p>
                  <Button
                    onClick={async () => {
                      setMetransExporting(true);
                      try {
                        const response = await axios.get(
                          `${API_URL}/pricing/metrans/export-rates`,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `metrans_rates_${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        toast.success(`Exported ${response.data.rates_count} Metrans rates`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Export failed");
                      } finally {
                        setMetransExporting(false);
                      }
                    }}
                    disabled={metransExporting}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="export-metrans-btn"
                  >
                    {metransExporting ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Metrans Rates
                  </Button>
                </div>

                {/* Import Section */}
                <div className="space-y-3">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    <Upload className="h-4 w-4 text-orange-600" />
                    Import Rates
                  </h3>
                  <p className="text-sm text-slate-600">
                    Upload JSON (from export) or CSV file with rates
                  </p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json,.csv,.xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          setMetransImporting(true);
                          setMetransResults(null);
                          
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(
                              `${API_URL}/import/metrans-rates?import_mode=update`,
                              formData,
                              {
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'multipart/form-data',
                                },
                              }
                            );
                            
                            setMetransResults(response.data);
                            toast.success(`Imported ${response.data.imported} rates, updated ${response.data.updated}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Import failed");
                          } finally {
                            setMetransImporting(false);
                          }
                        }}
                        disabled={metransImporting}
                      />
                      <Button
                        asChild
                        variant="outline"
                        disabled={metransImporting}
                        className="border-orange-300 hover:bg-orange-100"
                      >
                        <span>
                          {metransImporting ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Import (Update Mode)
                        </span>
                      </Button>
                    </label>
                    
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json,.csv,.xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          if (!window.confirm('⚠️ This will DELETE all existing Metrans rates and replace with the file contents. Continue?')) {
                            return;
                          }
                          
                          setMetransImporting(true);
                          setMetransResults(null);
                          
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(
                              `${API_URL}/import/metrans-rates?import_mode=fresh`,
                              formData,
                              {
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'multipart/form-data',
                                },
                              }
                            );
                            
                            setMetransResults(response.data);
                            toast.success(`Deleted ${response.data.deleted}, imported ${response.data.imported} rates`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Import failed");
                          } finally {
                            setMetransImporting(false);
                          }
                        }}
                        disabled={metransImporting}
                      />
                      <Button
                        asChild
                        variant="destructive"
                        disabled={metransImporting}
                      >
                        <span>
                          {metransImporting ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Fresh Import (Replace All)
                        </span>
                      </Button>
                    </label>
                  </div>
                  
                  {/* Import Results */}
                  {metransResults && (
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="font-medium text-sm">Import Results</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        {metransResults.deleted !== undefined && (
                          <div className="text-center">
                            <p className="font-bold text-red-600">{metransResults.deleted}</p>
                            <p className="text-slate-500">Deleted</p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="font-bold text-green-600">{metransResults.imported}</p>
                          <p className="text-slate-500">Imported</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-blue-600">{metransResults.updated}</p>
                          <p className="text-slate-500">Updated</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-slate-400">{metransResults.skipped}</p>
                          <p className="text-slate-500">Skipped</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* CSV Column Reference */}
              <div className="mt-4 p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>CSV columns:</strong> country, destination_city, postal_code, price_20, price_40, price_40hc, direction (import/export)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Full Database Backup Section */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Database className="h-5 w-5 text-indigo-500" />
            Full Database Backup
          </h2>
          <Card className="bg-gradient-to-br from-indigo-50 to-slate-50 border-indigo-200">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-800">Export All Collections</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Download complete database backup as JSON (includes all collections)
                  </p>
                  {backupDownloading && (
                    <p className="text-sm text-indigo-600 mt-2 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Preparing backup... This may take a few minutes for large databases.
                    </p>
                  )}
                </div>
                <Button
                  onClick={async () => {
                    if (backupDownloading) return;
                    
                    setBackupDownloading(true);
                    toast.info("Starting database backup download...");
                    
                    try {
                      // Use fetch API with streaming for large files - more reliable than axios for big downloads
                      const response = await fetch(`${API_URL}/admin/database/export`, {
                        method: 'GET',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                        },
                      });
                      
                      if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.detail || `HTTP ${response.status}: Backup failed`);
                      }
                      
                      // Get the blob from response
                      const blob = await response.blob();
                      
                      // Create download URL and trigger download
                      const downloadUrl = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = downloadUrl;
                      a.download = `myeuropacific_backup_${new Date().toISOString().split('T')[0]}.json`;
                      document.body.appendChild(a);
                      a.click();
                      
                      // Cleanup
                      setTimeout(() => {
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(downloadUrl);
                      }, 100);
                      
                      toast.success("Database backup downloaded successfully!");
                    } catch (error) {
                      console.error("Backup download error:", error);
                      if (error.name === 'TypeError' && error.message.includes('network')) {
                        toast.error("Network error - please check your connection and try again");
                      } else {
                        toast.error(error.message || "Backup download failed - please try again");
                      }
                    } finally {
                      setBackupDownloading(false);
                    }
                  }}
                  disabled={backupDownloading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="full-backup-btn"
                >
                  {backupDownloading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download Full Backup
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Required Columns Reference */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">Import Column Reference</h2>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Module</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Key Field</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-700">Required Columns</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {modules.map((module) => (
                  <tr key={module.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{module.name}</td>
                    <td className="px-4 py-3">
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                        {module.keyField}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {module.requiredCols.map((col, idx) => (
                        <code key={col} className="bg-slate-100 px-2 py-0.5 rounded text-xs mr-1">
                          {col}
                        </code>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExportPage;
